import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { carsApi } from '../api/cars.js';
import { teamApi } from '../api/team.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PlateNumber } from '../components/ui/PlateNumber.jsx';

const USAGE = [
  { value: 'personal', label: 'Personal' },
  { value: 'ridesharing', label: 'Ridesharing' },
  { value: 'inchiriat', label: 'Închiriat' },
  { value: 'altele', label: 'Altele' },
];

export function CarForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [form, setForm] = useState({
    numar_inmatriculare: '',
    marca: '',
    model: '',
    an: '',
    kilometraj: '',
    tip_utilizare: 'personal',
    observatii: '',
    assigned_user_id: '',
  });
  const [users, setUsers] = useState([]);
  const [existingPhotoId, setExistingPhotoId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    teamApi.listUsers().then((r) => setUsers(r.users)).catch(() => setUsers([]));
  }, [isAdmin]);

  useEffect(() => {
    if (!isEdit) return;
    carsApi.get(id)
      .then((data) => {
        const c = data.car;
        setForm({
          numar_inmatriculare: c.numar_inmatriculare,
          marca: c.marca,
          model: c.model,
          an: c.an ?? '',
          kilometraj: c.kilometraj ?? '',
          tip_utilizare: c.tip_utilizare,
          observatii: c.observatii ?? '',
          assigned_user_id: c.assigned_user_id ?? '',
        });
        if (c.poza_path) setExistingPhotoId(c.id);
      })
      .catch((ex) => setErr(ex.body?.error || 'Eroare'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  useEffect(() => {
    if (!photoFile) return setPreview(null);
    const url = URL.createObjectURL(photoFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const fd = new FormData();
      for (const [k, v] of Object.entries(form)) {
        // assigned_user_id: dacă admin și vrea neasignat, trimitem string gol ca să-l recunoască backendul
        if (k === 'assigned_user_id') {
          if (isAdmin) fd.append(k, v || '');
          continue;
        }
        if (v !== '' && v !== null && v !== undefined) fd.append(k, v);
      }
      if (photoFile) fd.append('poza', photoFile);

      const res = isEdit ? await carsApi.update(id, fd) : await carsApi.create(fd);
      navigate(`/cars/${res.car.id}`);
    } catch (ex) {
      if (ex.body?.error === 'conflict') setErr('Există deja o mașină cu acest număr în firma ta.');
      else if (ex.body?.error === 'invalid_mime') setErr('Fișier invalid — doar JPEG/PNG/WebP pentru poză.');
      else if (ex.body?.error === 'file_too_large') setErr('Poza depășește 5MB.');
      else if (ex.body?.error === 'validation_error') setErr('Date invalide. Verifică câmpurile.');
      else setErr(ex.body?.error || 'Eroare la salvare.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="card text-center text-muted">Se încarcă…</div>;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-semibold tracking-tight text-ink">
          {isEdit ? 'Editează mașina' : 'Mașină nouă'}
        </h2>
        <Link to={isEdit ? `/cars/${id}` : '/cars'} className="text-sm text-muted underline">Anulează</Link>
      </div>

      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

      <div className="card space-y-4">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted">Număr înmatriculare</span>
          <input
            required
            maxLength={15}
            value={form.numar_inmatriculare}
            onChange={(e) => setForm({ ...form, numar_inmatriculare: e.target.value.toUpperCase() })}
            placeholder="B 123 ABC"
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 font-mono text-base tracking-wider focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
          {form.numar_inmatriculare && (
            <div className="mt-2">
              <PlateNumber value={form.numar_inmatriculare.replace(/\s+/g, '')} size="lg" />
            </div>
          )}
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Marcă</span>
            <input
              required
              value={form.marca}
              onChange={(e) => setForm({ ...form, marca: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Model</span>
            <input
              required
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">An</span>
            <input
              type="number"
              min={1900}
              max={2100}
              value={form.an}
              onChange={(e) => setForm({ ...form, an: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Kilometraj</span>
            <input
              type="number"
              min={0}
              max={10000000}
              value={form.kilometraj}
              onChange={(e) => setForm({ ...form, kilometraj: e.target.value })}
              placeholder="km"
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Utilizare</span>
            <select
              value={form.tip_utilizare}
              onChange={(e) => setForm({ ...form, tip_utilizare: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            >
              {USAGE.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </label>
        </div>

        {isAdmin && (
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Asignează către</span>
            <select
              value={form.assigned_user_id}
              onChange={(e) => setForm({ ...form, assigned_user_id: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            >
              <option value="">Neasignat (doar admin)</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email}) {u.role === 'admin' ? '— admin' : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted">
              Dacă asignezi către un membru, doar el va vedea mașina. Adminii văd toată flota.
            </p>
          </label>
        )}

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted">Observații (opțional)</span>
          <textarea
            rows={3}
            maxLength={2000}
            value={form.observatii}
            onChange={(e) => setForm({ ...form, observatii: e.target.value })}
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>

        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-muted">Poză (JPEG/PNG/WebP, max 5MB)</span>
          <div className="mt-2 flex items-center gap-3">
            <div className="h-[72px] w-[72px] flex-shrink-0 overflow-hidden rounded-[14px] bg-cream">
              {preview ? (
                <img src={preview} alt="" className="h-full w-full object-cover" />
              ) : existingPhotoId ? (
                <img src={carsApi.photoUrl(existingPhotoId)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-2xl text-border">⛃</div>
              )}
            </div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
              className="text-sm"
            />
          </div>
        </div>
      </div>

      <button disabled={busy} className="btn-primary w-full">
        {busy ? 'Se salvează…' : (isEdit ? 'Salvează modificările' : 'Adaugă mașina')}
      </button>
    </form>
  );
}
